export interface ScenarioItem {
  id: string;
  scenario: string;
  scenarioTag: string;
  icon: string;
  xmValue: string;
  xmDetail: string;
  fxtmValue: string;
  fxtmDetail: string;
  winner: "xm" | "fxtm" | "tie";
  reason: string;
}

export const scenarioData: ScenarioItem[] = [
  {
    id: "scalping",
    scenario: "Heavy Scalping / High-Frequency",
    scenarioTag: "Scalping",
    icon: "ri-flashlight-line",
    xmValue: "EUR/USD spread ~0.6 pip (Ultra Low), zero commission",
    xmDetail: "Ultra Low account delivers spreads from 0.6 pips with zero commission — ~$6/lot all-in. 99.35% of orders executed in under 1 second with no-requote policy.",
    fxtmValue: "EUR/USD spread from 0.0 pip + $3.50/side (Advantage)",
    fxtmDetail: "Advantage account offers raw interbank spreads from 0.0 pips with true ECN execution — no dealing desk intervention. Average fill time ~71ms with 99.90% of orders executed in under 1 second. Commission at $3.50 per side ($7 round-turn) means ~$7/lot all-in when spreads average 0.0–0.1 pip. Price improvement technology means you may get filled at better-than-requested prices.",
    winner: "fxtm",
    reason: "FXTM Wins: Raw ECN at ~71ms with 99.90% fill rate is purpose-built for scalping. At ~$7/lot all-in with true interbank pricing, the $1/lot premium over XM Ultra Low (~$6/lot) buys you no-dealing-desk execution, faster fills, and direct market access — worth it for high-frequency traders."
  },
  {
    id: "beginner",
    scenario: "Small Capital / Getting Started",
    scenarioTag: "Beginners",
    icon: "ri-seedling-line",
    xmValue: "$5 min + $30 no-deposit bonus + 100% welcome bonus",
    xmDetail: "Start trading with as little as $5 — the lowest barrier among major brokers. $30 no-deposit bonus lets you test real markets risk-free. 100% welcome bonus up to $500 doubles your first deposit. Massive educational library with live webinars, video tutorials, and multi-language support including Chinese.",
    fxtmValue: "$10 Micro cent account + 100% welcome bonus up to $500",
    fxtmDetail: "Micro account starts at just $10 with cent-based trading — 1 cent lot = 1,000 units, perfect for risk-controlled practice. 100% welcome deposit match bonus up to $500 (promo code HELLOFXTM) matches your first deposit in reward points. No no-deposit bonus available. Educational resources include daily market analysis and platform tutorials.",
    winner: "xm",
    reason: "XM Wins: Both offer 100% deposit match bonuses, but XM adds a $30 no-deposit bonus and a $5 minimum — you can start trading without depositing a cent. FXTM's $10 Micro is very accessible too (far better than the $200 required for Advantage accounts), but XM's no-deposit bonus gives true zero-risk entry that FXTM doesn't match."
  },
  {
    id: "swing",
    scenario: "Large Capital / Swing & Hold",
    scenarioTag: "Swing Trading",
    icon: "ri-moon-line",
    xmValue: "ASIC/CySEC/FSC multi-regulated, 1,000+ instruments",
    xmDetail: "Triple-regulated with top-tier ASIC and CySEC oversight plus FSC. 1,000+ instruments across forex, stocks, indices, commodities, and crypto. Standard overnight swap fees at industry average. Reliable for multi-week position holds with segregated client funds at major banks and negative balance protection.",
    fxtmValue: "FSC Mauritius + FSCA South Africa, 1,000+ instruments",
    fxtmDetail: "Primary regulation through FSC Mauritius and FSCA South Africa. FCA entity (Exinity UK Ltd) is ceasing operations in 2026. CySEC license voluntarily renounced, operations ceased December 2023. Still maintains segregated client funds and negative balance protection. 1,000+ instruments across multiple asset classes with competitive swap rates.",
    winner: "xm",
    reason: "XM Wins: ASIC/CySEC tier-1 regulation provides stronger investor protection and regulatory oversight than FSC Mauritius alone — this matters for long-term position holders with significant capital at risk. FXTM's regulatory mix is adequate for day-to-day trading, but for multi-week swing holds with larger accounts, tier-1 regulatory backing provides crucial peace of mind."
  },
  {
    id: "news",
    scenario: "News Trading / High-Impact Events",
    scenarioTag: "News Events",
    icon: "ri-newspaper-line",
    xmValue: "99.35% execution under 1 second, no requotes",
    xmDetail: "99.35% of orders executed in under 1 second. Strict no-requote policy across all account types. Consistent execution during NFP, FOMC, and high-volatility events with controlled slippage. Market-maker model with tight slippage guardrails.",
    fxtmValue: "~71ms avg execution, 99.90% fill rate, ECN model",
    fxtmDetail: "Average execution speed of ~71ms with 99.90% of trades filled in under 1 second. ECN model means fills reflect real market conditions — spreads may widen during extreme events but execution remains transparent with no dealing desk intervention. Price improvement technology can deliver better-than-requested fills.",
    winner: "fxtm",
    reason: "FXTM Wins: ~71ms average execution is significantly faster than XM's 1-second window — during fast-moving news events like NFP or FOMC, every millisecond counts. 99.90% fill rate and ECN transparency give news traders direct market access without dealing desk interference, even when volatility spikes."
  }
];

export interface IndicatorItem {
  label: string;
  icon: string;
  xmScore: number;
  xmNote: string;
  fxtmScore: number;
  fxtmNote: string;
}

export const indicatorData: IndicatorItem[] = [
  {
    label: "Deposit & Withdrawal Speed",
    icon: "ri-speed-line",
    xmScore: 8.5,
    xmNote: "Manual review, typically processed within hours on business days. Multiple methods supported with no fees from XM's side.",
    fxtmScore: 8.0,
    fxtmNote: "Most methods processed same-day. No deposit/withdrawal fees from FXTM's side. However: 3% fee applies if withdrawing without placing any trades since last deposit. Bank wires take 1-3 business days depending on intermediary banks."
  },
  {
    label: "Maximum Leverage",
    icon: "ri-rocket-line",
    xmScore: 8.0,
    xmNote: "Up to 1:1000 — stable and sufficient for most strategies. Fixed across account types.",
    fxtmScore: 9.0,
    fxtmNote: "Up to 1:3000 on Advantage/Advantage Plus (international entities). Dynamic/floating — reduces as position volume increases. Micro: fixed per instrument class (Majors 1:1000). Advantage Stocks: 1:1 (no leverage). Note: Kenya residents capped at 1:400; during major news events leverage may temporarily drop to 1:200."
  },
  {
    label: "Minimum Deposit",
    icon: "ri-wallet-3-line",
    xmScore: 9.5,
    xmNote: "$5 across all forex accounts — the industry's lowest barrier. Also offers $30 no-deposit bonus for zero-cost entry.",
    fxtmScore: 6.5,
    fxtmNote: "$10 for Micro cent account (lowest entry). $200 for Advantage/Advantage Plus/Stocks accounts. No no-deposit bonus available."
  },
  {
    label: "All-in Trading Cost (EUR/USD)",
    icon: "ri-money-dollar-circle-line",
    xmScore: 7.5,
    xmNote: "Standard/Micro: 1.0 pip spread. Ultra Low: 0.6 pips + $0 commission (~$6/lot). Micro: 1.0 pip.",
    fxtmScore: 8.0,
    fxtmNote: "Advantage: 0.0 pip raw + $7/lot round-turn (~$7/lot). Advantage Plus: from 1.5 pips + $0 commission (~$15/lot). Micro: from 1.5 pips, zero commission."
  },
  {
    label: "Regulatory Security",
    icon: "ri-shield-check-line",
    xmScore: 9.0,
    xmNote: "ASIC, CySEC, FSC multi-regulated. Segregated client funds at major banks. Negative balance protection standard. Tier-1 oversight.",
    fxtmScore: 7.0,
    fxtmNote: "FSC Mauritius + FSCA South Africa (primary). FCA entity (Exinity UK Ltd) ceasing operations 2026. CySEC voluntarily renounced, EU operations ceased Dec 2023. Segregated funds maintained."
  },
  {
    label: "Platform & Tool Ecosystem",
    icon: "ri-tools-line",
    xmScore: 8.0,
    xmNote: "MT4/MT5 with TradingView chart integration. Free VPS for qualifying EA traders. MQL5 community signals for copy trading.",
    fxtmScore: 7.5,
    fxtmNote: "MT4/MT5 with proprietary FXTM Trader mobile app. FXTM Invest for curated copy trading. No cTrader or native TradingView direct trading integration."
  }
];

export interface DownsideItem {
  text: string;
  icon: string;
}

export interface BrokerHonestTake {
  pros: DownsideItem[];
  cons: DownsideItem[];
}

export const xmHonestTake: BrokerHonestTake = {
  pros: [
    {
      text: "$30 no-deposit bonus lets you trade real markets risk-free. 100% welcome bonus up to $500 doubles your first deposit. No other major broker offers this combination.",
      icon: "ri-check-line"
    },
    {
      text: "99.35% of orders executed in under 1 second. Strict no-requote policy means virtually zero rejected orders, even during major news events like NFP and FOMC.",
      icon: "ri-check-line"
    },
    {
      text: "Ultra Low account with zero commissions and spreads from 0.6 pips — ~$6/lot all-in. The sweet spot for cost-conscious traders who want tight pricing without commission math.",
      icon: "ri-check-line"
    },
    {
      text: "ASIC, CySEC, FSC multi-regulated with segregated client funds and negative balance protection. Tier-1 regulatory framework gives long-term position holders real peace of mind.",
      icon: "ri-check-line"
    },
    {
      text: "XMP loyalty points on every trade — the more you trade, the more you earn back in cash or bonus credit. Free VPS for qualifying EA traders running 24/7 algorithms.",
      icon: "ri-check-line"
    }
  ],
  cons: [
    {
      text: "Standard and Micro accounts carry 1.0 pip spreads — decent but not industry-leading. Without the no-deposit bonus or Ultra Low account, XM's raw spread pricing blends into the pack against ECN competitors.",
      icon: "ri-emotion-sad-line"
    },
    {
      text: "Limited cryptocurrency pairs and fewer exotic forex pairs compared to dedicated crypto or forex-specialist brokers.",
      icon: "ri-stock-line"
    },
    {
      text: "Bonus terms come with trading volume requirements (lot turnover). The free money isn't withdrawable until you trade enough — read the fine print before counting on it.",
      icon: "ri-file-list-3-line"
    }
  ]
};

export const fxtmHonestTake: BrokerHonestTake = {
  pros: [
    {
      text: "Advantage account delivers raw interbank spreads from 0.0 pips with true ECN execution — no dealing desk, no markup, no requotes. ~71ms average fill speed with 99.90% of orders in under 1 second.",
      icon: "ri-check-line"
    },
    {
      text: "Maximum leverage up to 1:3000 on Advantage/Advantage Plus (international entities, floating from 1:100) — among the highest in the forex industry. Dynamic/tiered system adjusts based on position size. Micro account uses fixed leverage per instrument class: Majors 1:1000, Minors 1:500, Exotics 1:50.",
      icon: "ri-check-line"
    },
    {
      text: "100% welcome deposit match bonus up to $500 (promo code HELLOFXTM). Matches your first deposit in reward points — effectively double your initial capital during the bonus period.",
      icon: "ri-check-line"
    },
    {
      text: "FXTM Invest — proprietary copy trading platform connecting investors with vetted strategy managers. Only pay profit share when managers are profitable. Full transparency on manager track records and risk metrics — more curated than open marketplace signals.",
      icon: "ri-check-line"
    },
    {
      text: "Micro account at just $10 for cent-based beginners, Advantage Stocks for zero-commission stock CFD trading, and four account types spanning every trader profile from cent-level practice to professional ECN execution.",
      icon: "ri-check-line"
    }
  ],
  cons: [
    {
      text: "Regulatory footprint is narrowing: CySEC license voluntarily renounced (EU operations ceased Dec 2023), FCA entity (Exinity UK Ltd) is ceasing operations in 2026. Primary regulation now through FSC Mauritius and FSCA South Africa — fewer investor protection schemes than tier-1 regulators like ASIC or FCA.",
      icon: "ri-alert-line"
    },
    {
      text: "$200 minimum deposit on Advantage, Advantage Plus, and Advantage Stocks accounts creates a real barrier for small-capital beginners. While the $10 Micro account exists, you need $200+ to access FXTM's best trading conditions.",
      icon: "ri-money-dollar-circle-line"
    },
    {
      text: "$5 monthly inactivity fee after 6 months of no trading activity. Seasonal or occasional traders who step away from the markets will see their balance slowly eroded if they forget to log in.",
      icon: "ri-time-line"
    },
    {
      text: "3% withdrawal fee applies if you request a withdrawal without placing any trades since your last deposit — designed to prevent misuse, but catches out traders who fund their account but haven't started trading yet.",
      icon: "ri-exchange-funds-line"
    }
  ]
};

export interface AccountMatchup {
  id: string;
  title: string;
  subtitle: string;
  xmAccount: {
    name: string;
    spread: string;
    commission: string;
    minDeposit: string;
    leverage: string;
    highlight: string;
    pros: string[];
  };
  fxtmAccount: {
    name: string;
    spread: string;
    commission: string;
    minDeposit: string;
    leverage: string;
    highlight: string;
    pros: string[];
  };
  analysis: string;
}

export const accountMatchups: AccountMatchup[] = [
  {
    id: "lowcost",
    title: "The True Cost Battle: Who's Cheaper Per Lot?",
    subtitle: "XM Ultra Low Account vs FXTM Advantage Account — raw numbers, no marketing fluff",
    xmAccount: {
      name: "XM Ultra Low",
      spread: "EUR/USD from 0.6 pips",
      commission: "Zero commission",
      minDeposit: "$5",
      leverage: "Up to 1:1000",
      highlight: "Pure spread model — zero commission",
      pros: ["Zero commission — all cost is the spread", "Lowest-cost XM account at ~0.6 pip spreads", "$5 minimum deposit — near-zero barrier", "1,000+ instruments across all asset classes"]
    },
    fxtmAccount: {
      name: "FXTM Advantage",
      spread: "EUR/USD from 0.0 pips (raw interbank)",
      commission: "$3.50/side ($7/lot round-turn)",
      minDeposit: "$200",
      leverage: "Up to 1:3000 (floating, international entities)",
      highlight: "Raw ECN — true interbank pricing",
      pros: ["Raw interbank spreads from 0.0 pips — no markup", "True ECN execution — no dealing desk intervention", "~71ms avg execution, 99.90% orders under 1 second", "Up to 1:3000 floating leverage"]
    },
    analysis: "Let's do the math: XM Ultra Low at 0.6 pips + $0 commission = ~$6/lot all-in. FXTM Advantage at avg ~0.1 pip + $7/lot = ~$8/lot all-in. On pure cost, XM Ultra Low is ~$2/lot cheaper. But FXTM Advantage delivers true ECN execution at ~71ms with 99.90% fill rates — for high-frequency scalpers, the superior execution quality and raw interbank pricing can recoup that $2/lot premium through better fills. For moderate traders doing 5-10 lots/day, XM Ultra Low's cost advantage is real and meaningful. For heavy scalpers, FXTM's ECN infrastructure is worth the premium."
  },
  {
    id: "popular",
    title: "The Flagship Face-Off: Which Default Account Wins?",
    subtitle: "XM Standard Account vs FXTM Advantage Plus — the most-used accounts, head to head",
    xmAccount: {
      name: "XM Standard",
      spread: "EUR/USD from 1.0 pip",
      commission: "Zero commission",
      minDeposit: "$5",
      leverage: "Up to 1:1000",
      highlight: "XM's most popular — bonus eligible",
      pros: ["Zero commission — pay only the spread", "Eligible for $30 no-deposit + 100% welcome bonus", "Full standard lot (100,000 units) trading", "99.35% execution rate with no-requote policy"]
    },
    fxtmAccount: {
      name: "FXTM Advantage Plus",
      spread: "EUR/USD from 1.5 pips",
      commission: "Zero commission",
      minDeposit: "$200",
      leverage: "Up to 1:3000 (floating, international)",
      highlight: "FXTM's commission-free flagship",
      pros: ["Zero commission — pay only the spread", "From 1.5 pips — slightly tighter than XM Standard's 1.6", "Up to 1:3000 floating leverage — triple XM's maximum", "FXTM Invest copy trading + 100% welcome bonus eligible"]
    },
    analysis: "This one is closer than it looks. FXTM Advantage Plus at 1.5 pips is wider than XM Standard's 1.0 pip — but both are zero-commission. The trade-off: XM requires just $5 and gives you a $30 no-deposit bonus on top of the 100% match — perfect for getting started. FXTM requires $200 but gives you 1:3000 leverage (3x XM's), and access to the FXTM Invest copy trading ecosystem. For small accounts, XM wins on accessibility and tighter standard spreads. For traders with $200+ who value extreme leverage and copy trading, FXTM Advantage Plus pulls ahead."
  },
  {
    id: "stocks",
    title: "The Stocks Showdown: Physical vs CFD",
    subtitle: "XM Shares Account vs FXTM Advantage Stocks — two completely different philosophies",
    xmAccount: {
      name: "XM Shares",
      spread: "Exchange-level real stock spreads",
      commission: "Per-share commission (varies by exchange)",
      minDeposit: "$10,000",
      leverage: "No leverage (physical share ownership)",
      highlight: "1,200+ global stocks — real ownership",
      pros: ["1,200+ individual shares across global exchanges", "Dividend rights included on long positions", "Physical share ownership — not CFDs", "Access to US, UK, EU stock exchanges"]
    },
    fxtmAccount: {
      name: "FXTM Advantage Stocks",
      spread: "From 0.1 pips",
      commission: "Zero commission",
      minDeposit: "$200",
      leverage: "1:1 (no leverage)",
      highlight: "Zero commission — stock CFDs on MT5",
      pros: ["Zero commission on all stock CFD trades", "Over 350 US and European stocks available on MT5", "1:1 leverage — invest without borrowed funds", "$200 minimum deposit — accessible entry"]
    },
    analysis: "Two completely different products. XM Shares gives you physical stock ownership with exchange pricing, dividend rights, and real shareholder status — but requires $10,000 to start and has no leverage. FXTM Advantage Stocks is a zero-commission stock investing account with 1:1 leverage — you trade at the share's actual value without borrowed funds, similar to a cash brokerage account. $200 entry makes it far more accessible, but you never own the underlying shares and there are no dividend entitlements. For serious stock investors with $10K+ capital, XM Shares wins on authenticity and ownership rights. For investors who want stock market exposure without tying up large capital, FXTM Advantage Stocks opens the door at just $200 — but with 1:1 leverage, it's an investing account, not a leveraged trading tool."
  }
];

export interface TechComparison {
  category: string;
  icon: string;
  xm: { label: string; detail: string; score: number };
  fxtm: { label: string; detail: string; score: number };
}

export const techData: TechComparison[] = [
  {
    category: "Trading Platform Options",
    icon: "ri-computer-line",
    xm: { label: "MT4/MT5 + TradingView Charts", detail: "Full MT4 and MT5 support with MQL community signals. TradingView integration for chart-based analysis. No proprietary platform. Free VPS for qualifying EA traders.", score: 7.5 },
    fxtm: { label: "MT4/MT5 + Proprietary App", detail: "Full MT4/MT5 support. Proprietary FXTM Trader mobile app for account management and market analysis. No cTrader or native TradingView direct trading integration. VPS available for algorithmic traders.", score: 7.0 }
  },
  {
    category: "Copy Trading Ecosystem",
    icon: "ri-group-line",
    xm: { label: "MQL5 Community Signals", detail: "Copy trading primarily through MQL5 community signals. No dedicated proprietary copy trading platform. Functional but relies on third-party signal providers with varying quality and no centralized vetting.", score: 5.5 },
    fxtm: { label: "FXTM Invest — Proprietary Platform", detail: "FXTM Invest is a dedicated proprietary copy trading platform connecting investors with vetted strategy managers. Investors only pay profit share when managers are profitable. Full transparency on manager track records, risk scores, and historical performance — more curated than open marketplace signals.", score: 8.5 }
  },
  {
    category: "Mobile App Experience",
    icon: "ri-smartphone-line",
    xm: { label: "Standard MT4/MT5 Mobile", detail: "Relies on standard MetaTrader mobile apps for trading. Solid for order management and basic charting but no proprietary mobile experience. Functional but not differentiated.", score: 7.0 },
    fxtm: { label: "FXTM Trader + MT Apps", detail: "Proprietary FXTM Trader app alongside MT4/MT5 mobile. Cleaner interface with built-in account management, faster deposits/withdrawals, and integrated market analysis. Better mobile experience than standard MT apps alone.", score: 7.5 }
  },
  {
    category: "API & Automation Support",
    icon: "ri-code-line",
    xm: { label: "MT4/MT5 Standard API", detail: "Full MQL4/MQL5 support with rich community resources. Free VPS for EA traders meeting volume requirements. No standalone REST API or FIX protocol access for retail clients.", score: 7.5 },
    fxtm: { label: "MT4/MT5 Standard API", detail: "Full MQL4/MQL5 support with ECN liquidity for algorithmic execution. VPS hosting available. No standalone REST API or FIX protocol access for retail clients — similar automation capabilities to XM.", score: 7.5 }
  }
];

export interface PerkItem {
  icon: string;
  title: string;
  description: string;
}

export const xmPerks: PerkItem[] = [
  {
    icon: "ri-gift-2-line",
    title: "$30 No-Deposit Bonus",
    description: "Claim it right after account verification. Trade real markets without depositing a cent first — the ultimate risk-free test drive. No other major broker offers a comparable no-strings-attached entry point."
  },
  {
    icon: "ri-vip-diamond-line",
    title: "Loyalty Points Program (XMP)",
    description: "Earn points on every trade you make. Redeem accumulated points for cash credit or bonus balance. The more you trade, the more you earn back — effectively lowering your net spread costs over time."
  },
  {
    icon: "ri-cloud-line",
    title: "Free VPS Service",
    description: "Qualify with minimum deposit and volume requirements. Perfect for traders running Expert Advisors 24/7 without keeping their personal computer on — ensures algorithms never miss a tick."
  },
  {
    icon: "ri-percent-line",
    title: "100% Welcome Deposit Bonus",
    description: "Double your first deposit up to $500. Deposit $500, trade with $1,000. Combined with the $30 no-deposit bonus, XM offers the most aggressive bonus package in the retail forex industry."
  }
];

export const fxtmPerks: PerkItem[] = [
  {
    icon: "ri-rocket-2-line",
    title: "1:3000 Maximum Leverage (Floating)",
    description: "FXTM's international entities offer up to 1:3000 floating leverage on Advantage and Advantage Plus accounts — triple XM's maximum of 1:1000. Floating from 1:100 upwards, the ratio adjusts dynamically as notional position value increases. Micro accounts use fixed leverage per instrument class (Majors 1:1000).",
  },
  {
    icon: "ri-speed-mini-line",
    title: "~71ms Execution + 99.90% Fill Rate",
    description: "Orders execute in ~71ms on average with 99.90% of trades filled in under 1 second. ECN model with price improvement technology means you may get filled at better-than-requested prices — real savings on every trade."
  },
  {
    icon: "ri-user-shared-line",
    title: "FXTM Invest — Curated Copy Trading",
    description: "A dedicated proprietary copy trading platform connecting investors with vetted strategy managers. Only pay profit share when managers are profitable. Full transparency on track records and risk metrics — more curated than open marketplace signal providers."
  },
  {
    icon: "ri-bank-card-line",
    title: "100% Welcome Deposit Match Bonus",
    description: "Get your first deposit matched up to $500 in reward points (promo code HELLOFXTM). Earn $50 in points for every 2,000 Tier Points generated through trading. 30-day window to claim the full reward — effectively doubles your starting capital during the bonus period."
  }
];

export interface BlackSwanPoint {
  category: string;
  xm: string;
  fxtm: string;
  xmAdvantage: boolean;
}

export const blackSwanData: BlackSwanPoint[] = [
  {
    category: "Order Execution (News Events)",
    xm: "99.35% of orders executed in under 1 second. Zero-requote policy across all account types. Rock-solid during NFP, FOMC, and high-volatility events with strict slippage control — market-maker model provides execution consistency during chaos.",
    fxtm: "Average ~71ms execution with 99.90% fill rate. ECN model means fills reflect real market conditions — spreads may widen during extreme volatility but execution remains transparent with no dealing desk interference. Price improvement technology can deliver better-than-requested fills.",
    xmAdvantage: true
  },
  {
    category: "Slippage Management",
    xm: "Explicitly guarantees no requotes. Strict slippage control across all account types with market-maker oversight. Most orders filled at requested price or better with minimal deviation — consistency is prioritized over raw speed.",
    fxtm: "ECN execution with price improvement technology. During normal conditions, many trades receive better-than-requested prices. However, during extreme events, ECN models naturally experience more slippage as spreads gap — the trade-off for pure market access.",
    xmAdvantage: true
  },
  {
    category: "Leverage Stability During Crises",
    xm: "1:1000 maximum leverage remains consistent with fixed parameters. Traders can rely on pre-set risk parameters without surprise adjustments. ASIC/CySEC oversight ensures leverage policies stay predictable.",
    fxtm: "1:3000 floating leverage is generous but decreases as position volume increases. During high-volatility events (e.g., NFP), FXTM applies Dynamic Margin Requirements that temporarily restrict leverage — sometimes down to 1:200. More flexible but less predictable during crises.",
    xmAdvantage: true
  },
  {
    category: "Fund Security & Regulatory Protection",
    xm: "ASIC and CySEC regulation provides tier-1 investor protection with compensation schemes. Segregated client funds at major banks. Negative balance protection standard. Multi-jurisdiction regulatory oversight with strong enforcement frameworks.",
    fxtm: "FSC Mauritius and FSCA South Africa provide regulatory oversight with segregated client funds. FCA entity (Exinity UK Ltd) is ceasing operations in 2026, and CySEC license was voluntarily renounced in 2024. Fewer investor compensation schemes compared to tier-1 regulators like ASIC or FCA.",
    xmAdvantage: false
  }
];

export interface QuizQuestion {
  id: string;
  question: string;
  options: { label: string; value: string }[];
}

export const quizQuestions: QuizQuestion[] = [
  {
    id: "capital",
    question: "What's your starting capital?",
    options: [
      { label: "Under $100 — Just testing the waters", value: "small" },
      { label: "$100 - $5,000 — Got a decent budget", value: "medium" },
      { label: "$5,000+ — Professional level", value: "large" }
    ]
  },
  {
    id: "style",
    question: "What's your trading style?",
    options: [
      { label: "Scalping / High-frequency — Dozens to hundreds of trades daily", value: "scalping" },
      { label: "Day trading / Swing — A few trades per day to per week", value: "moderate" },
      { label: "Long-term investing / Passive — Hold positions for weeks to months", value: "swing" }
    ]
  },
  {
    id: "priority",
    question: "What matters most to you?",
    options: [
      { label: "Raw ECN execution and maximum leverage — pure market access", value: "ecn" },
      { label: "Bonuses, low entry barrier, and strong regulation — safety first", value: "safety" },
      { label: "Copy trading and passive income — let pros do the work", value: "copy" }
    ]
  }
];

export function computeQuizResult(answers: Record<string, string>): { broker: "xm" | "fxtm"; percentage: number; reason: string } {
  const { capital, style, priority } = answers;
  let xmScore = 0;
  let fxtmScore = 0;

  if (style === "scalping") {
    fxtmScore += 35;
    xmScore += 10;
  } else if (style === "moderate") {
    xmScore += 20;
    fxtmScore += 20;
  } else {
    xmScore += 25;
    fxtmScore += 10;
  }

  if (capital === "small") {
    xmScore += 30;
    fxtmScore += 5;
  } else if (capital === "large") {
    fxtmScore += 15;
    xmScore += 15;
  } else {
    xmScore += 15;
    fxtmScore += 15;
  }

  if (priority === "ecn") {
    fxtmScore += 30;
    xmScore += 5;
  } else if (priority === "safety") {
    xmScore += 30;
    fxtmScore += 5;
  } else {
    fxtmScore += 25;
    xmScore += 10;
  }

  const total = xmScore + fxtmScore;
  if (xmScore >= fxtmScore) {
    return {
      broker: "xm",
      percentage: Math.round((xmScore / total) * 100),
      reason: xmScore > fxtmScore * 1.3
        ? "Your trading style is a strong match for XM: $5 minimum deposit, $30 no-deposit bonus, 100% welcome match, and ASIC/CySEC tier-1 regulation. Perfect for beginners, safety-conscious traders, and anyone who wants to start small with strong regulatory protection."
        : "You lean toward XM, but FXTM is worth a look too. XM's ultra-low entry barrier and bonus program give you a head start. But if you want raw ECN execution at ~71ms and 1:3000 leverage, check out FXTM's Advantage account before deciding."
    };
  }
  return {
    broker: "fxtm",
    percentage: Math.round((fxtmScore / total) * 100),
    reason: fxtmScore > xmScore * 1.3
      ? "Your trading style is built for FXTM: raw ECN execution at ~71ms, 1:3000 floating leverage, proprietary FXTM Invest copy trading, and a 100% welcome match bonus. Perfect for scalpers, leverage traders, and anyone who wants true interbank pricing without the dealing desk."
      : "You lean toward FXTM, but XM's $5 entry and $30 no-deposit bonus are worth considering. FXTM's raw ECN Advantage account, ~71ms execution, and 1:3000 leverage are excellent — but if you're starting with pocket money, XM's zero-cost entry through the no-deposit bonus is hard to beat."
  };
}

export interface AccountTypeItem {
  name: string;
  highlight: string;
  spread: string;
  commission: string;
  minDeposit: string;
  leverage: string;
  note: string;
  features: string[];
  bestFor: string;
}

export const xmAccountTypes: AccountTypeItem[] = [
  {
    name: "Micro Account",
    highlight: "Ultra-low entry barrier",
    spread: "From 1.0 pip (EUR/USD)",
    commission: "Zero commission",
    minDeposit: "$5",
    leverage: "Up to 1:1000",
    note: "Contracts are 1,000 base currency units (1 micro lot). Perfect for absolute beginners who want to learn with minimal risk. Same execution quality as larger accounts — no discrimination based on account size. 99.35% of orders executed in under 1 second, same across all XM account types.",
    features: [
      "Trade with as little as $5 — the industry's lowest barrier",
      "1 micro lot = 1,000 units, ideal for position sizing practice",
      "Full MT4/MT5 access with all 1,000+ instruments",
      "Negative balance protection included as standard",
      "Same no-requote policy as all XM accounts"
    ],
    bestFor: "Complete beginners testing the waters with pocket change"
  },
  {
    name: "Standard Account",
    highlight: "Most popular — bonus eligible",
    spread: "From 1.0 pip (EUR/USD)",
    commission: "Zero commission",
    minDeposit: "$5",
    leverage: "Up to 1:1000",
    note: "XM's flagship account. 1 standard lot = 100,000 units. No commissions, just the raw spread. Eligible for all XM bonuses including $30 no-deposit and 100% welcome match. Reliable spread-only model backed by 99.35% execution rates and zero-requote policy.",
    features: [
      "Zero commission — pay only the spread, nothing more",
      "1 standard lot = 100,000 units, full-size forex trading",
      "Eligible for all bonuses: $30 no-deposit + 100% welcome match",
      "Free daily market analysis, webinars, and educational content",
      "XMP loyalty points earned on every trade — trade more, earn more"
    ],
    bestFor: "Everyday traders who want bonuses + simple no-commission pricing"
  },
  {
    name: "Ultra Low Account",
    highlight: "Tightest XM spreads — zero commission",
    spread: "From 0.6 pips (EUR/USD)",
    commission: "Zero commission",
    minDeposit: "$50",
    leverage: "Up to 1:1000",
    note: "XM's tightest spread offering. Spreads start at 0.6 pips on EUR/USD — nearly one-third the Standard account — with zero commissions. Available in both Micro and Standard lot sizes. For moderate to active traders who want tighter pricing without per-trade commission calculations. ~$6/lot all-in on EUR/USD.",
    features: [
      "Spreads from 0.6 pips — XM's tightest pure-spread offering",
      "Zero commission — all cost is in the spread, simple and transparent",
      "Available in both Micro and Standard lot sizes",
      "Same 1,000+ instruments and platform access",
      "Ideal middle ground between Micro entry and professional pricing"
    ],
    bestFor: "Cost-aware traders wanting tight spreads without commission math"
  },
  {
    name: "Shares Account",
    highlight: "1,200+ global stocks — real ownership",
    spread: "Exchange-level real stock spreads",
    commission: "Per-share commission (varies by exchange)",
    minDeposit: "$10,000",
    leverage: "No leverage (physical shares)",
    note: "XM's dedicated stock trading account. Direct access to 1,200+ individual shares across US, UK, and EU exchanges — not CFDs, actual share trading with ownership rights and dividend entitlements. Leverage is not available by design — you own the underlying shares outright.",
    features: [
      "Trade 1,200+ individual shares across global exchanges",
      "Exchange-grade pricing with transparent per-share commissions",
      "Dividend rights included on long positions",
      "Access to US, UK, EU stock exchanges",
      "Physical share ownership — fundamentally different from CFDs"
    ],
    bestFor: "Serious stock investors who want real exchange access, not CFD exposure"
  }
];

export const fxtmAccountTypes: AccountTypeItem[] = [
  {
    name: "Advantage Micro Account",
    highlight: "Cent-based — lowest FXTM entry",
    spread: "From 1.5 pips (EUR/USD)",
    commission: "Zero commission",
    minDeposit: "$10",
    leverage: "Fixed: 1:1000 (Majors), 1:500 (Minors), 1:50 (Exotics)",
    note: "FXTM's entry-level cent account. Trade in cents rather than dollars — 1 cent lot = 1,000 base currency units. Designed for beginners who want real-market practice with minimal financial risk. Full MT4 platform access. Zero commission with spreads from 1.5 pips. Leverage is fixed per instrument class: Majors at 1:1000, Minors at 1:500, Exotics at 1:50, and Spot Metals at 1:500. Note: Micro accounts are only available to clients in specific regions (including Nigeria and Kenya). Not available for EU or UK residents.",
    features: [
      "Cent-based trading — 1 cent lot = 1,000 units for precise sizing",
      "$10 minimum deposit — lowest FXTM entry point",
      "Zero commission — pay only the spread",
      "Full MT4 platform access with all major instruments",
      "Islamic swap-free version available on request"
    ],
    bestFor: "Absolute beginners who want cent-based practice with minimal risk"
  },
  {
    name: "Advantage Plus Account",
    highlight: "Commission-free — popular flagship",
    spread: "From 1.5 pips (EUR/USD)",
    commission: "Zero commission",
    minDeposit: "$200",
    leverage: "Up to 1:3000 (floating, international entities)",
    note: "FXTM's most popular account — zero commission with spreads from 1.5 pips. All cost is baked into the spread for simple, straightforward pricing. Access to FXTM Invest copy trading. Eligible for 100% welcome deposit match bonus. Slightly tighter than XM Standard's 1.6 pips at the same zero-commission structure.",
    features: [
      "Zero commission — pay only the spread, nothing more",
      "From 1.5 pips — slightly tighter than many competitors' standard accounts",
      "Up to 1:3000 floating leverage on international entities",
      "FXTM Invest copy trading access",
      "Eligible for 100% welcome deposit match bonus up to $500"
    ],
    bestFor: "Traders who want simple pricing + high leverage + copy trading access"
  },
  {
    name: "Advantage Account",
    highlight: "Raw ECN — spreads from 0.0 pip",
    spread: "From 0.0 pips (EUR/USD, raw interbank)",
    commission: "$3.50 per side ($7 per round turn)",
    minDeposit: "$200",
    leverage: "Up to 1:3000 (floating, international entities)",
    note: "FXTM's premium ECN account. Raw interbank spreads from 0.0 pips with ~71ms average execution and 99.90% fill rate. True ECN model — no dealing desk, no markup, trades routed directly to liquidity providers. For professional scalpers, algorithmic traders, and anyone who demands pure market access. ~$7/lot all-in when spreads average 0.0–0.1 pip.",
    features: [
      "Raw interbank spreads from 0.0 pips — true ECN pricing",
      "$3.50/side commission — competitive ECN rate",
      "~71ms avg execution with 99.90% orders under 1 second",
      "Price improvement technology — may get better-than-requested fills",
      "All strategies welcome — scalping, hedging, EAs, algorithmic trading"
    ],
    bestFor: "Active traders and scalpers who want raw ECN with high leverage"
  },
  {
    name: "Advantage Stocks Account",
    highlight: "Zero commission — stock CFDs",
    spread: "From 0.1 pips",
    commission: "Zero commission",
    minDeposit: "$200",
    leverage: "1:1 (no leverage)",
    note: "FXTM's dedicated stock investing account on the MT5 platform. Zero commission on over 350 US and European stock CFDs. 1:1 leverage — trade at the share's actual value without borrowed funds. Unlike XM Shares which offers physical stock ownership with dividends, this is CFD trading — no ownership rights or dividend entitlements, but far lower entry barrier at $200. Spreads from 0.1 pips on share products.",
    features: [
      "Zero commission on all stock CFD trades",
      "Over 350 US and European stocks available as CFDs on MT5",
      "1:1 leverage — invest without borrowed funds",
      "$200 minimum deposit — highly accessible vs $10K for physical shares",
      "Full MT5 platform with advanced charting and analysis tools"
    ],
    bestFor: "Investors who want stock market exposure at a low entry point without leverage"
  }
];